// client/src/components/tracker/DealEngagements.tsx
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { apiRequest } from "@/lib/queryClient";
import { deriveEv, formatEvRange, formatMultipleRange, type OfferTerms } from "@shared/offerMath";

interface Engagement {
  clientId: string; clientName: string; clientEbitda: number | null;
  furthestMilestone: string | null; furthestDate: string | null;
  ioiTerms: OfferTerms | null; loiTerms: OfferTerms | null;
}

// Permanent bid history: what this buyer offered when they reached IOI/LOI.
function BidLine({ label, terms, ebitda }: { label: string; terms: OfferTerms; ebitda: number | null }) {
  const ev = deriveEv(terms, ebitda);
  return (
    <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-600 dark:bg-slate-950/40 dark:text-slate-400">
      <span className="font-bold text-slate-700 dark:text-slate-400">{label}</span>{" "}
      {formatEvRange(ev.low, ev.high)}{" "}
      <span className="font-semibold text-blue-700 dark:text-blue-400">({formatMultipleRange(terms.multipleLow, terms.multipleHigh)})</span>
    </span>
  );
}

/**
 * IOI and LOI terms per client, shown beside the furthest milestone on the
 * buyer's processes table. Terms only exist past an NDA, which is all
 * /deal-engagements returns.
 */
export function useBidLines(buyerId: string): Map<string, ReactNode> {
  const { data } = useQuery<Engagement[]>({
    queryKey: ["deal-engagements", buyerId],
    queryFn: () => apiRequest("GET", `/api/companies/${buyerId}/deal-engagements`).then((r) => r.json()),
  });
  return useMemo(() => new Map(
    (data ?? []).filter((e) => e.ioiTerms || e.loiTerms).map((e) => [e.clientId, (
      <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
        {e.ioiTerms && <BidLine label="IOI" terms={e.ioiTerms} ebitda={e.clientEbitda} />}
        {e.loiTerms && <BidLine label="LOI" terms={e.loiTerms} ebitda={e.clientEbitda} />}
      </span>
    )] as [string, ReactNode]),
  ), [data]);
}
